import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { reportError } from '../observability/reportError';

function is401(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as Record<string, unknown>).status === 401
  );
}

/**
 * A budget refusal (ADR-0078 §4) is the product working as configured, not an app
 * error: the composer shows it, and reporting it would trip the create-flow alarm.
 */
function is402(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as Record<string, unknown>).status === 402
  );
}

function onQueryError(error: unknown, query: { queryKey: unknown }): void {
  if (is401(error)) {
    window.dispatchEvent(new CustomEvent('agentisai:auth:reauth-required'));
    return;
  }
  reportError(error, { source: 'queryCache', queryKey: query.queryKey });
}

function onMutationError(
  error: unknown,
  _vars: unknown,
  _ctx: unknown,
  mutation: { options: { mutationKey?: unknown } },
): void {
  if (is401(error)) {
    window.dispatchEvent(new CustomEvent('agentisai:auth:reauth-required'));
    return;
  }
  if (is402(error)) return;
  reportError(error, { source: 'mutationCache', queryKey: mutation.options.mutationKey });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onMutationError }),
  defaultOptions: {
    queries: {
      // Non-zero staleTime: avoids refetching on every mount/tab-focus for
      // the create-chat surface. Tuned short — chat turns are ephemeral.
      staleTime: 10_000,
      // Long gcTime keeps warm cache on quick route revisits.
      gcTime: 5 * 60_000,
      // Bounded retry; never retry 4xx — they won't succeed.
      retry: (failureCount, error) => {
        if (is401(error)) return false;
        if (
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          typeof (error as { status: unknown }).status === 'number' &&
          (error as { status: number }).status >= 400 &&
          (error as { status: number }).status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
      // Explicit choice: refetch on window focus is driven by staleTime, not
      // a blanket disable — individual queries can override if needed.
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Writes must not silently re-fire.
      retry: 0,
    },
  },
});
